import { useDatabase } from "@nozbe/watermelondb/hooks";
import { withObservables } from "@nozbe/watermelondb/react";
import Sale, { SaleState } from "../../../../utilities/data/model/Sale";
import { SaleControllerContext } from "../../utils/sale-controller";
import { useEffect, useState } from "react";
import { useActiveTerminal } from "../../../../utilities/app-state/hooks";
import { useWithAppState } from "@towersystems/roam-common-native";
import { of } from "rxjs";
import { useInvoiceSequenceProvider } from "../../../../utilities/invoice-sequence";
import { generateInvoiceNumber } from "../../../../utilities/generate-invoice-number";
import { SyncState } from "../../../../utilities/data/constants";
import Staff from "../../../../utilities/data/model/Staff";
import { Q } from "@nozbe/watermelondb";
import { useMeProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";

// clean up this file=
export interface SaleControllerProps {
  children?: React.ReactNode;
}

interface EnhancedSaleController extends SaleControllerProps {
  saleId?: string;
  sale?: Sale;
}

const enhance = withObservables(["sale"], ({ sale }: any) => {
  return {
    sale: sale ? sale : of(null),
  };
});

const PSaleController = ({ children, sale }: EnhancedSaleController) => {
  const database = useDatabase();

  const {
    methods: { getAndIncrementInvoiceSequence },
  } = useInvoiceSequenceProvider();

  const { me } = useMeProvider();

  const { terminal } = useActiveTerminal();

  const { updateAppState, appState } = useWithAppState();

  const createEmptySale = async (): Promise<Sale> => {
    if (!terminal) {
      throw new Error("No terminal selected");
    }

    const invoiceSequence = await getAndIncrementInvoiceSequence(terminal.id);

    const maybeStaff = (
      await database
        .get<Staff>("staffs")
        .query(Q.where("user_id", Q.eq(me.id)))
        .fetch()
    )[0];

    const newSale = await database.write(async () => {
      const newSale = await database.get<Sale>("sales").create((s) => {
        s.invoiceNumber = generateInvoiceNumber(
          invoiceSequence,
          terminal.salePrefix || "",
          terminal.saleSufix || ""
        );
        s.invoiceSequence = invoiceSequence;
        s.syncState = SyncState.MODIFED;
        s.saleState = SaleState.OPEN;
        s.numSyncAttempts = 0;
        s.location.id = appState.locationId;

        if (maybeStaff) {
          s.staff.id = maybeStaff.id;
        }

        s.terminal.id = appState.terminalId;
        s.payments = [];
      });

      updateAppState({ localSaleId: newSale.id });
      return newSale;
    });
    return newSale;
  };

  return (
    <SaleControllerContext.Provider value={{ sale: sale, createEmptySale }}>
      {children}
    </SaleControllerContext.Provider>
  );
};

const EnhancedSaleController = enhance(PSaleController);

export const SaleController = (props: SaleControllerProps) => {
  const [sale, setSale] = useState<Sale | undefined>();
  const { appState, updateAppState } = useWithAppState();
  const { localSaleId } = appState;

  const database = useDatabase();

  useEffect(() => {
    if (localSaleId) {
      localSaleId &&
        database
          .get<Sale>("sales")
          .find(localSaleId)
          .then((i) => setSale(i))
          .catch((e) => updateAppState({ localSaleId: undefined }));
    } else {
      setSale(undefined);
    }
  }, [localSaleId]);

  // this causes a complete re-render of the app
  // find a way to only re-render the sale controller
  return <EnhancedSaleController sale={sale} {...props} />;
};
